export const formatTime = (time: number): string => {
  if (!Number.isFinite(time) || Number.isNaN(time)) return '';
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  return `${hours > 0 ? `${hours}:` : ''}${minutes < 10 ? '0' : ''}${minutes}:${
    seconds < 10 ? '0' : ''
  }${seconds}`;
};

export const timeStringToSeconds = (timeStr: string): number => {
  const parts = timeStr.trim().split(':');

  if (parts.length === 2) {
    // MM:SS format
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    if (minutes < 60) {
      return minutes * 60 + seconds;
    }
    // Handle cases where minutes >= 60
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours * 3600 + remainingMinutes * 60 + seconds;
  }
  if (parts.length === 3) {
    // HH:MM:SS format
    const [hours, minutes, seconds] = parts.map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  }

  return 0;
};

export interface TimeSegment {
  start: number;
  end: number;
  description: string;
}

export const getTimeSegments = (timeStamps: Timestamp[]): TimeSegment[] => {
  // Sort timestamps by time to ensure proper ordering
  const sortedTimestamps = [...timeStamps].sort(
    (a, b) => timeStringToSeconds(a.time) - timeStringToSeconds(b.time)
  );

  // Remove any duplicate timestamps
  const uniqueTimestamps = sortedTimestamps.filter((stamp, index, array) => {
    if (index === 0) return true;
    return stamp.time !== array[index - 1].time;
  });

  return uniqueTimestamps.map((timeStamp, index) => {
    const startTime = timeStringToSeconds(timeStamp.time);
    const nextTimestamp = uniqueTimestamps[index + 1];
    const endTime = nextTimestamp
      ? timeStringToSeconds(nextTimestamp.time)
      : startTime + 1; // If no next timestamp, make segment 1 second long

    return {
      start: startTime,
      end: endTime,
      description: timeStamp.description,
    };
  });
};

export interface Timestamp {
  time: string;
  description: string;
}

export const parseTimestampsFromText = (text: string) => {
  const timestamps = text
    .split('\n')
    .filter((line: string) => line.trim())
    .map((line: string) => {
      // Match timestamps at the start of a line in various formats:
      // [HH:MM:SS], HH:MM:SS, (HH:MM:SS), * HH:MM:SS
      const timePattern = /^(?:\[(\d{2}:\d{2}:\d{2})\]|\((\d{2}:\d{2}:\d{2})\)|\*\s*(\d{2}:\d{2}:\d{2})|(\d{2}:\d{2}:\d{2}))/;
      const match = line.trim().match(timePattern);

      if (!match) return null;

      // Find the first non-null capturing group which will be our timestamp
      const timestamp = match[1] || match[2] || match[3] || match[4];

      // Get the description after removing all timestamps and any formatting
      const description = line
        .trim()
        .replace(/\[?\(?\*?\s*\d{2}:\d{2}:\d{2}\]?\)?/g, '') // Remove all timestamps
        .replace(/^\s*-\s*/, '') // Remove dash if present
        .replace(/\s+/g, ' ')
        .trim();

      return [
        {
          time: timestamp,
          description,
        },
      ];
    })
    .filter((item): item is Timestamp[] => item !== null)
    .flat();

  return getTimeSegments(timestamps);
};
